// Rate figures for the v2 duty slip (2026-09-26).
//
// The dialog edits the trip's rate copy IN ITS CANONICAL SHAPE — the one the
// pricing engine reads and the backend's shape guard checks — so a custom
// save sends exactly what will be priced, with no field-name translation:
//
//   { baseRate, surCharge, platformCharge, rateConfig: { ...inner } }
//
//   airport inner:  pricingType (perKm | flatRate | flatPlusKm), flatAmount,
//                   minKm, base_rate_per_km, nightAllowance.rate,
//                   earlyStartAllowance.rate
//   package inner:  cost, duration, durationType, maxDistanceKms,
//                   extraHrs.{graceValue,rate}, extraKM.{graceValue,rate},
//                   nightAllowance.rate, earlyStartAllowance.rate,
//                   driverAllowance.rate, additionalCharges[i].rate
//
// Each field is addressed by a path into that object. The same paths drive
// the "card changed since saved" comparison, which by construction ignores
// everything that is not a rate: ids, names, garage legs, times, distances —
// the backend's PASSTHROUGH_KEYS.

export const AIRPORT_PRICING_TYPES = [
  ['perKm', '/km'],
  ['flatRate', 'Flat Rate'],
  ['flatPlusKm', 'Flat Rate + /km'],
];

const BASE_FARE = { path: ['baseRate'], label: 'Base Fare', unit: '₹' };
const OUTER = [
  BASE_FARE,
  { path: ['surCharge'], label: 'Surcharge', unit: '₹' },
  { path: ['platformCharge'], label: 'Platform Charge', unit: '₹' },
];

const ALLOWANCES = [
  { path: ['rateConfig', 'nightAllowance', 'rate'], label: 'Night Allowance', unit: '₹' },
  {
    path: ['rateConfig', 'earlyStartAllowance', 'rate'],
    label: 'Early Start Allowance',
    unit: '₹',
  },
];

const airportFields = () => [
  {
    path: ['rateConfig', 'pricingType'],
    label: 'Rate Type',
    select: AIRPORT_PRICING_TYPES,
  },
  {
    path: ['rateConfig', 'flatAmount'],
    label: 'Flat Rate',
    unit: '₹',
    forType: ['flatRate', 'flatPlusKm'],
  },
  { path: ['rateConfig', 'minKm'], label: 'Included KM', unit: 'km', forType: ['flatPlusKm'] },
  {
    path: ['rateConfig', 'base_rate_per_km'],
    label: '/KM',
    unit: '₹',
    forType: ['perKm', 'flatPlusKm'],
  },
  // A flat airport card's fare IS its flat amount, so the engine leaves the
  // row's base fare out of the subtotal and reports it as 0 — the rate card
  // form hides the column for these shapes and it may hold a stale figure
  // (Pricing.util.ts: `isFlatAirport ? 0 : baseRate`). Offering a box for a
  // number that is thrown away is worse than not offering it: it was typed
  // into, the fare did not move, and nothing said why.
  { ...BASE_FARE, forType: ['perKm'] },
  ...OUTER.filter((f) => f !== BASE_FARE),
  ...ALLOWANCES,
];

const packageFields = (dutyType, copy) => {
  const perDay = Number(dutyType) === 7 || copy?.rateConfig?.durationType === 'day';
  const fields = [
    { path: ['rateConfig', 'cost'], label: perDay ? 'Price / day' : 'Price', unit: '₹' },
    {
      path: ['rateConfig', 'duration'],
      label: perDay ? 'Days' : 'Hours',
      unit: perDay ? 'day' : 'hr',
      // An outstation package is always priced per day and the company form
      // fixes its duration at one; the number of days a trip actually ran is
      // worked out by the engine from the trip's own times, not typed here.
      fixed: Number(dutyType) === 7,
    },
    {
      path: ['rateConfig', 'maxDistanceKms'],
      label: perDay ? 'Max Distance / day' : 'Included KM',
      unit: 'km',
    },
    ...OUTER,
    { path: ['rateConfig', 'extraHrs', 'graceValue'], label: 'Grace Hour', unit: 'hr' },
    { path: ['rateConfig', 'extraHrs', 'rate'], label: 'Extra price / Hour', unit: '₹' },
    { path: ['rateConfig', 'extraKM', 'graceValue'], label: 'Grace Km', unit: 'km' },
    { path: ['rateConfig', 'extraKM', 'rate'], label: 'Extra price / Km', unit: '₹' },
    ...ALLOWANCES,
  ];
  if (perDay || copy?.rateConfig?.driverAllowance) {
    fields.push({
      path: ['rateConfig', 'driverAllowance', 'rate'],
      label: perDay ? 'Driver Allowance / day' : 'Driver Allowance',
      unit: '₹',
    });
  }
  const extras = Array.isArray(copy?.rateConfig?.additionalCharges)
    ? copy.rateConfig.additionalCharges
    : [];
  extras.forEach((item, i) => {
    fields.push({
      path: ['rateConfig', 'additionalCharges', i, 'rate'],
      label: item?.label || item?.name || `Additional charge ${i + 1}`,
      unit: '₹',
    });
  });
  return fields;
};

export const fieldsFor = (dutyType, copy) =>
  Number(dutyType) === 4 ? airportFields() : packageFields(dutyType, copy);

// A field is shown when it has no rate-type condition, or the copy's airport
// pricing type is one it applies to.
export const fieldVisible = (field, copy) =>
  !field.forType || field.forType.includes(copy?.rateConfig?.pricingType);

export const getAt = (obj, path) =>
  path.reduce((acc, key) => (acc === undefined || acc === null ? undefined : acc[key]), obj);

// Immutable deep set; creates intermediate objects (or arrays for numeric keys).
export const setAt = (obj, path, value) => {
  if (!path.length) return value;
  const [key, ...rest] = path;
  const base = obj && typeof obj === 'object' ? obj : typeof key === 'number' ? [] : {};
  const next = Array.isArray(base) ? [...base] : { ...base };
  next[key] = setAt(base[key], rest, value);
  return next;
};

// The backend's PASSTHROUGH_KEYS: things that ride inside a rate copy without
// being rates. Never part of the "has the card changed" comparison.
const PASSTHROUGH = new Set([
  'id',
  'name',
  'carTypeId',
  'rateCardTypeId',
  'sourceRateCardId',
  'pricingMode',
  'garage',
  'kilometersIn',
  'kilometersOut',
  'fixedGarageRate',
  'timeIn',
  'timeOut',
  'distance',
  'eta',
  'isMultiDay',
]);

const numeric = (v) =>
  v !== null && v !== undefined && `${v}`.trim() !== '' && Number.isFinite(Number(v));

const leafEqual = (a, b) => {
  const aEmpty = a === null || a === undefined || `${a}`.trim() === '';
  const bEmpty = b === null || b === undefined || `${b}`.trim() === '';
  if (aEmpty && bEmpty) return true;
  // Rate columns come back as strings from the card table and as numbers from
  // the trip's copy: "45" must equal 45, and a missing grace equals 0.
  if ((aEmpty || numeric(a)) && (bEmpty || numeric(b))) {
    return Number(aEmpty ? 0 : a) === Number(bEmpty ? 0 : b);
  }
  return `${a}` === `${b}`;
};

const deepEqualRates = (a, b) => {
  const aObj = a && typeof a === 'object';
  const bObj = b && typeof b === 'object';
  if (!aObj && !bObj) return leafEqual(a, b);
  if (!aObj || !bObj) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (PASSTHROUGH.has(key)) continue;
    if (!deepEqualRates(a[key], b[key])) return false;
  }
  return true;
};

// Do two rate copies price the same? Outer columns plus the inner object,
// passthrough keys ignored.
export const sameRates = (a, b) =>
  leafEqual(a?.baseRate, b?.baseRate) &&
  leafEqual(a?.surCharge, b?.surCharge) &&
  leafEqual(a?.platformCharge, b?.platformCharge) &&
  deepEqualRates(a?.rateConfig || {}, b?.rateConfig || {});

// A card row from GET /company/v2/rate-cards, as the dropdown shows it.
export const cardLabel = (card) =>
  card?.rateConfig?.name ||
  card?.rateCardType?.name ||
  (typeof card?.rateCardType === 'string' ? card.rateCardType : null) ||
  card?.name ||
  (card?.rateConfig?.pricingType
    ? `Airport ${AIRPORT_PRICING_TYPES.find(([k]) => k === card.rateConfig.pricingType)?.[1] || ''}`.trim()
    : `Rate card ${String(card?.id || '').slice(0, 8)}`);

export const clone = (v) => (v === undefined ? undefined : JSON.parse(JSON.stringify(v)));

// Put a rate card onto the trip's copy. A card row carries rates and nothing
// else: it has no garage, no garage legs, no measured distance or eta, and no
// outer baseRatePerKM (the card keeps its per-km rate at rateConfig
// .base_rate_per_km; rateCalculator derives the outer column when a card is
// applied at booking). Replacing the trip's copy with the card wholesale
// therefore drops the garage legs — the fare loses its garage charge — and
// leaves the fare engine's per-km branch, which reads the outer
// baseRatePerKM, with nothing to read. So take the rates from the card and
// keep everything that describes THIS trip from the copy it replaces, then
// carry the per-km rate out to the column the engine reads.
export const cardOntoTrip = (card, tripCopy) => {
  if (!card) return clone(tripCopy);
  const next = clone(card);
  PASSTHROUGH.forEach((key) => {
    if (tripCopy?.[key] !== undefined) next[key] = clone(tripCopy[key]);
  });
  // The card IS the basis now, so its own identity wins over the trip's.
  ['id', 'name', 'rateCardTypeId'].forEach((key) => {
    if (card[key] !== undefined) next[key] = clone(card[key]);
  });
  delete next.sourceRateCardId;
  const perKm = next?.rateConfig?.base_rate_per_km;
  if (perKm !== undefined && perKm !== null && `${perKm}`.trim() !== '') {
    next.baseRatePerKM = Number(perKm);
  } else {
    // A card with no per-km rate must not inherit the outer column from the
    // copy it replaced: the engine reads that column before it looks at the
    // package, so a figure left over from an airport copy prices a rental as
    // a per-km airport fare.
    delete next.baseRatePerKM;
  }
  return next;
};

// The rate fields a duty type prices on, blank, keeping everything that is a
// fact about the trip rather than a rate. Used when the duty type changes and
// the new type has no card to pick: the old copy cannot be carried across
// because the shapes share no keys — an airport copy has no package, a package
// copy no flat rate — so the figures start empty and the operator supplies
// them, while the garage legs and measured distances survive.
export const blankRateFor = (dutyType, tripCopy) => {
  const next = {};
  PASSTHROUGH.forEach((key) => {
    if (tripCopy?.[key] !== undefined) next[key] = clone(tripCopy[key]);
  });
  delete next.id;
  delete next.name;
  delete next.rateCardTypeId;
  delete next.sourceRateCardId;
  // Surcharge and platform charge mean the same thing on every duty type, so
  // they carry; the base fare is part of what is being re-decided.
  next.baseRate = '';
  next.surCharge = tripCopy?.surCharge ?? '';
  next.platformCharge = tripCopy?.platformCharge ?? '';
  // Allowances are configured per company, not per duty type, so whatever the
  // trip was carrying stays — an operator correcting the shape of a trip has
  // not changed who the driver is or when they started.
  const inner = {};
  ['nightAllowance', 'earlyStartAllowance'].forEach((key) => {
    if (tripCopy?.rateConfig?.[key] !== undefined) {
      inner[key] = clone(tripCopy.rateConfig[key]);
    }
  });
  if (Number(dutyType) === 4) {
    inner.pricingType = 'flatRate';
    inner.flatAmount = '';
  } else {
    inner.cost = '';
    // Outstation is always one day at a time — the field is fixed, so seed it
    // rather than leaving a blank the operator cannot fill.
    inner.duration = Number(dutyType) === 7 ? 1 : '';
    inner.durationType = Number(dutyType) === 7 ? 'day' : 'hour';
    inner.maxDistanceKms = '';
    inner.extraKM = { rate: '', graceValue: '' };
    inner.extraHrs = { rate: '', graceValue: '' };
  }
  next.rateConfig = inner;
  return next;
};
